import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/Calendar.css';

const TYPE_META = {
  komok:        { label: 'KOMOK',     color: '#00ff41' },
  kook:         { label: 'KOOK',      color: '#f5c518' },
  watch:        { label: 'WATCH',     color: '#4cc9ff' },
  concert:      { label: 'CONCERT',   color: '#ff6ec7' },
  social:       { label: 'HANG',      color: '#ffaa00' },
  trip:         { label: 'TRIP',      color: '#7bdf6a' },
};

const EVENTS = [
  /* === MAY === */
  {
    id: 'sean-brewery',
    date: '2026-05-16',
    title: "Sean's Brewery Crawl",
    location: 'Vancouver — TBD',
    type: 'social',
    icon: '🍺',
    confirmed: true,
  },
  {
    id: 'workshop-inaug',
    date: '2026-05-26',
    startTime: '19:11',
    endTime: '22:22',
    title: 'KOMOK Workshop Inauguration',
    subtitle: 'Inaugural hackerspace opening',
    location: 'KOMOK Workshop, Vancouver',
    type: 'komok',
    icon: '🛠️',
    confirmed: true,
    highlight: true,
    link: '/workshop',
    linkLabel: 'RSVP / book a tool →',
  },

  /* === JUNE — FIFA Group Stage === */
  {
    id: 'fifa-can-bos',
    date: '2026-06-12',
    startTime: '12:00 PST',
    title: 'Canada vs Bosnia & Herzegovina',
    subtitle: 'FIFA World Cup 2026 · Group Stage',
    location: 'Home or bar (TBD)',
    type: 'watch',
    icon: '⚽',
    confirmed: true,
    highlight: true,
  },

  /* === JULY === */
  {
    id: 'fifa-r32',
    date: '2026-07-02',
    startTime: '20:00',
    title: 'Round of 32 Watch Party',
    subtitle: 'FIFA World Cup 2026',
    location: 'Hastings Park, Vancouver',
    type: 'watch',
    icon: '⚽',
    confirmed: true,
    highlight: true,
  },
  {
    id: 'fifa-third',
    date: '2026-07-18',
    startTime: '14:00',
    title: 'Third Place Game',
    subtitle: 'FIFA World Cup 2026 · maybe',
    location: 'TBD',
    type: 'watch',
    icon: '⚽',
    confirmed: false,
  },
  {
    id: 'fifa-final',
    date: '2026-07-19',
    startTime: '12:00 PST',
    title: 'World Cup Final',
    subtitle: 'FIFA World Cup 2026 · maybe',
    location: 'TBD',
    type: 'watch',
    icon: '🏆',
    confirmed: false,
  },

  /* === PNE CONCERTS — only ones we're going to === */
  { id: 'pne-beaches', date: '2026-08-25', title: 'The Beaches',        location: 'PNE Summer Night Concerts', type: 'concert', icon: '🎤', confirmed: true, highlight: true },
  { id: 'pne-nelly',   date: '2026-08-26', title: 'Nelly',              location: 'PNE Summer Night Concerts', type: 'concert', icon: '🎤', confirmed: true, highlight: true },
  { id: 'pne-weirdal', date: '2026-09-03', title: 'Weird Al Yankovic',  location: 'PNE Summer Night Concerts', type: 'concert', icon: '🎤', confirmed: true, highlight: true },
  { id: 'pne-ewf',     date: '2026-09-04', title: 'Earth, Wind & Fire', location: 'PNE Summer Night Concerts', type: 'concert', icon: '🎤', confirmed: true, highlight: true },

  /* === MAYBES / TBD === */
  {
    id: 'boys-poker',
    date: '2026-05-23',
    startTime: '13:30',
    title: 'Boys Night · Poker',
    subtitle: 'Saturday afternoon poker — cards, snacks, trash talk',
    location: "Oskar's house",
    type: 'social',
    icon: '🃏',
    confirmed: true,
    highlight: true,
  },
  {
    id: 'van-island',
    date: 'TBD',
    title: 'Vancouver Island Trip',
    location: 'Vancouver Island',
    type: 'trip',
    icon: '🏝️',
    confirmed: false,
  },
];

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const WEEKDAYS = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

const isDated = (d) => /^\d{4}-\d{2}-\d{2}$/.test(d);

const parseDate = (d) => {
  if (!isDated(d)) return null;
  const [y, m, day] = d.split('-').map(Number);
  return new Date(y, m - 1, day);
};

const fmtMonth = (d) => `${MONTHS[d.getMonth()].toUpperCase()} ${d.getFullYear()}`;

/* ===== "Add to calendar" helpers ===== */
const PACIFIC_TZ = 'America/Los_Angeles';

const parseHHMM = (s) => {
  if (!s) return null;
  const cleaned = String(s).replace(/\s*(PST|PDT|EST|EDT|UTC|GMT)\s*$/i, '').trim();
  const m = cleaned.match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return null;
  return { h: parseInt(m[1], 10), m: parseInt(m[2], 10) };
};

const fmtLocalStamp = (yyyymmdd, h, mm) =>
  `${yyyymmdd.replace(/-/g, '')}T${String(h).padStart(2, '0')}${String(mm).padStart(2, '0')}00`;

const fmtDateOnly = (yyyymmdd) => yyyymmdd.replace(/-/g, '');

const addOneDay = (yyyymmdd) => {
  const [y, m, d] = yyyymmdd.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + 1);
  return `${dt.getUTCFullYear()}${String(dt.getUTCMonth() + 1).padStart(2, '0')}${String(dt.getUTCDate()).padStart(2, '0')}`;
};

const escapeICS = (s) =>
  String(s || '').replace(/\\/g, '\\\\').replace(/[;,]/g, (c) => '\\' + c).replace(/\n/g, '\\n');

const eventBounds = (e) => {
  const start = parseHHMM(e.startTime);
  const end = parseHHMM(e.endTime);
  if (!start) return null;
  let endH = end ? end.h : start.h + 2;
  let endM = end ? end.m : start.m;
  if (endH >= 24) endH = 23, endM = 59;
  return {
    startStr: fmtLocalStamp(e.date, start.h, start.m),
    endStr: fmtLocalStamp(e.date, endH, endM),
  };
};

const googleCalUrl = (e) => {
  if (!isDated(e.date)) return null;
  const bounds = eventBounds(e);
  let dates;
  if (bounds) {
    dates = `${bounds.startStr}/${bounds.endStr}`;
  } else {
    dates = `${fmtDateOnly(e.date)}/${addOneDay(e.date)}`;
  }
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: e.title,
    dates,
    ctz: PACIFIC_TZ,
    details: [e.subtitle, 'Added from komok.org/calendar'].filter(Boolean).join('\n'),
    location: e.location || '',
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
};

const buildIcs = (e) => {
  if (!isDated(e.date)) return null;
  const bounds = eventBounds(e);
  const dtstamp = new Date().toISOString().replace(/[-:]/g, '').replace(/\.\d+/, '');
  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//KOMOK//Calendar//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:${e.id}@komok.org`,
    `DTSTAMP:${dtstamp}`,
  ];
  if (bounds) {
    lines.push(`DTSTART;TZID=${PACIFIC_TZ}:${bounds.startStr}`);
    lines.push(`DTEND;TZID=${PACIFIC_TZ}:${bounds.endStr}`);
  } else {
    lines.push(`DTSTART;VALUE=DATE:${fmtDateOnly(e.date)}`);
    lines.push(`DTEND;VALUE=DATE:${addOneDay(e.date)}`);
  }
  lines.push(`SUMMARY:${escapeICS(e.title)}`);
  if (e.subtitle) lines.push(`DESCRIPTION:${escapeICS(e.subtitle)}`);
  if (e.location) lines.push(`LOCATION:${escapeICS(e.location)}`);
  lines.push('URL:https://komok.org/calendar');
  lines.push('END:VEVENT');
  lines.push('END:VCALENDAR');
  return lines.join('\r\n');
};

const downloadIcs = (e) => {
  const ics = buildIcs(e);
  if (!ics) return;
  const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${e.id}.ics`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1500);
};

const Calendar = () => {
  const navigate = useNavigate();
  const [filter, setFilter] = useState('all');
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const filtered = useMemo(() => {
    if (filter === 'all') return EVENTS;
    if (filter === 'maybe') return EVENTS.filter(e => !e.confirmed);
    return EVENTS.filter(e => e.type === filter);
  }, [filter]);

  // Group: dated events by month, then a "TBD" group at the bottom
  const groups = useMemo(() => {
    const dated = filtered.filter(e => isDated(e.date)).sort((a, b) => a.date.localeCompare(b.date));
    const tbd = filtered.filter(e => !isDated(e.date));
    const byMonth = {};
    const order = [];
    for (const e of dated) {
      const d = parseDate(e.date);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      if (!byMonth[key]) { byMonth[key] = { key, label: fmtMonth(d), items: [] }; order.push(key); }
      byMonth[key].items.push(e);
    }
    const out = order.map(k => byMonth[k]);
    if (tbd.length) out.push({ key: 'tbd', label: 'TBD', items: tbd });
    return out;
  }, [filtered]);

  const counts = useMemo(() => {
    const c = { all: EVENTS.length };
    for (const e of EVENTS) {
      c[e.type] = (c[e.type] || 0) + 1;
      if (!e.confirmed) c.maybe = (c.maybe || 0) + 1;
    }
    return c;
  }, []);

  const filters = [
    { id: 'all', label: 'All' },
    { id: 'komok', label: 'KOMOK' },
    { id: 'watch', label: 'Watch' },
    { id: 'concert', label: 'Concerts' },
    { id: 'social', label: 'Hangs' },
    { id: 'trip', label: 'Trips' },
    { id: 'maybe', label: 'Maybe' },
  ];

  return (
    <div className="cal-root">
      <div className="cal-topbar">
        <button className="cal-back" onClick={() => navigate('/')}>◀ HOME</button>
        <span>KOMOK//CALENDAR</span>
      </div>

      <div className="cal-header">
        <div className="cal-title">UPCOMING</div>
        <div className="cal-intro">
          KOMOK · KOOK · watch parties · concerts · trips. The whole summer.
        </div>
      </div>

      <div className="cal-filters">
        {filters.map(f => (
          counts[f.id] || f.id === 'all' ? (
            <button
              key={f.id}
              className={`cal-filter ${filter === f.id ? 'active' : ''}`}
              onClick={() => setFilter(f.id)}
            >
              {f.label}
              <span className="cal-filter-count">{counts[f.id] || 0}</span>
            </button>
          ) : null
        ))}
      </div>

      <div className="cal-list">
        {groups.length === 0 ? (
          <div className="cal-empty">No events match this filter.</div>
        ) : groups.map(g => (
          <section key={g.key} className="cal-month">
            <h2 className="cal-month-label">{g.label}</h2>
            <div className="cal-events">
              {g.items.map(e => {
                const meta = TYPE_META[e.type] || TYPE_META.social;
                const dt = parseDate(e.date);
                const isPast = dt && dt < today;
                const dayNum = dt ? dt.getDate() : '?';
                const weekday = dt ? WEEKDAYS[dt.getDay()] : '';
                const gUrl = googleCalUrl(e);
                const dated = isDated(e.date);
                const goLink = () => {
                  if (!e.link) return;
                  if (e.link.startsWith('/')) navigate(e.link);
                  else window.open(e.link, '_blank', 'noopener,noreferrer');
                };
                return (
                  <div
                    key={e.id}
                    className={`cal-event ${e.confirmed ? '' : 'maybe'} ${isPast ? 'past' : ''} ${e.highlight ? 'highlight' : ''} ${e.link ? 'has-link' : ''}`}
                    style={{ '--type-color': meta.color }}
                    onClick={e.link ? goLink : undefined}
                    role={e.link ? 'button' : undefined}
                    tabIndex={e.link ? 0 : undefined}
                    onKeyDown={e.link ? (ev) => { if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); goLink(); } } : undefined}
                  >
                    <div className="cal-date-pill">
                      <div className="cal-date-day">{dayNum}</div>
                      <div className="cal-date-wd">{weekday || 'TBD'}</div>
                    </div>
                    <div className="cal-event-body">
                      <div className="cal-event-head">
                        <span className="cal-event-icon" aria-hidden="true">{e.icon}</span>
                        <span className="cal-event-title">{e.title}</span>
                        {e.highlight && <span className="cal-star" aria-label="highlight">★</span>}
                      </div>
                      {e.subtitle && <div className="cal-event-sub">{e.subtitle}</div>}
                      <div className="cal-event-meta">
                        <span className="cal-event-type" style={{ color: meta.color, borderColor: meta.color }}>
                          {meta.label}
                        </span>
                        {e.startTime && (
                          <span className="cal-event-time">
                            {e.startTime}{e.endTime ? `–${e.endTime}` : ''}
                          </span>
                        )}
                        {e.location && <span className="cal-event-loc">📍 {e.location}</span>}
                        {!e.confirmed && <span className="cal-event-maybe">MAYBE</span>}
                      </div>

                      {(dated || e.link) && (
                        <div className="cal-event-actions" onClick={(ev) => ev.stopPropagation()}>
                          {dated && (
                            <a
                              href={gUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="cal-action-btn"
                              title="Add to Google Calendar"
                            >
                              <span className="cal-action-icon">＋</span> Google
                            </a>
                          )}
                          {dated && (
                            <button
                              type="button"
                              className="cal-action-btn"
                              onClick={() => downloadIcs(e)}
                              title="Download .ics for Apple/Outlook"
                            >
                              <span className="cal-action-icon">＋</span> Apple/iCal
                            </button>
                          )}
                          {e.link && (
                            <button type="button" className="cal-action-btn cal-action-btn-primary" onClick={goLink}>
                              {e.linkLabel || 'Details →'}
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        ))}
      </div>

      <div className="cal-footnote">
        Updates rolling. Tap KOMOK Workshop card on the home page to RSVP for the inauguration.
      </div>
    </div>
  );
};

export default Calendar;
